


import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {FETCH_PARTNER_MOBILE, TRAVEL_REGISTRATION } from '../../constants';
import companySymbol from '../../icons/company-symbol.png';
import PoultryConclave from '../../icons/poultrycoclave.jpg';

// API Base URL - replace with your actual API URL
// const API_BASE_URL = 'http://192.168.1.10:3005/api';

const FarmerTravelForm = () => {
  // Form data state
  const [formData, setFormData] = useState({
    mobileNumber: "",
    farmerName: "",
    farmCode: "",
    location: "",
    travelerName: "",
    alternateMobile: "",
    tShirtSize: "",
    travelMode: "",
    arrivalDateTime: "",
    agreeToTerms: false
  });

  // UI state management
  const [registeredUser, setRegisteredUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [numberChecked, setNumberChecked] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [apiError, setApiError] = useState('');

  // Validate mobile number format
  const isValidMobileNumber = (number) => {
    return /^[6-9]\d{9}$/.test(number);
  };

  // Check if mobile number is registered - API Call
  const checkMobileNumber = async () => {
    if (!formData.mobileNumber || !isValidMobileNumber(formData.mobileNumber)) {
      setValidationErrors(prev => ({
        ...prev,
        mobileNumber: "Please enter a valid 10-digit mobile number"
      }));
      return;
    }

    setLoading(true);
    setValidationErrors({});
    setApiError('');

    try {
      // API call to get partner details by mobile number
      // const response = await axios.get(`${API_BASE_URL}/partners/mobile/${formData.mobileNumber}`);

      const response = await axios.get(`${FETCH_PARTNER_MOBILE}/${formData.mobileNumber}`);
      if (response.data.status === "200") {
        const partnerData = response.data.data;
        setFormData(prev => ({
          ...prev,
          farmerName: partnerData.partnerName,
          farmCode: partnerData.partnerCode,
          location: partnerData.location
        }));
        setRegisteredUser(true);
      } else {
        setRegisteredUser(false);
      }
    } catch (error) {
      console.error("Error checking mobile number:", error);
      setRegisteredUser(false);
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.status === 404) {
          // Mobile number not found/registered
          setApiError("This mobile number is not registered. Please enter a registered mobile number or contact support for assistance.");
        } else {
          setApiError(`Error: ${error.response.data.message || 'Something went wrong'}`);
        }
      } else if (error.request) {
        // The request was made but no response was received
        setApiError("Network error. Please check your connection and try again.");
      } else {
        // Something happened in setting up the request that triggered an Error
        setApiError(`Error: ${error.message}`);
      }
    } finally {
      setNumberChecked(true);
      setLoading(false);
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle different input types
    if (type === "checkbox") {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name === "mobileNumber" || name === "alternateMobile") {
      // Only allow numbers and max 10 digits
      const phoneNumber = value.replace(/\D/g, "").slice(0, 10);
      setFormData(prev => ({
        ...prev,
        [name]: phoneNumber
      }));
      
      // Clear registered status when changing primary mobile
      if (name === "mobileNumber" && numberChecked) {
        setNumberChecked(false);
        setRegisteredUser(false);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear validation errors when field is updated
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle form submission - API Call
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = {};
    
    if (!registeredUser) {
      errors.mobileNumber = "Please verify your mobile number first";
    }
    
    if (!formData.travelerName.trim()) {
      errors.travelerName = "Traveler name is required";
    }
    
    if (!formData.alternateMobile || !isValidMobileNumber(formData.alternateMobile)) {
      errors.alternateMobile = "Valid alternate mobile number is required";
    }
    
    // if (!formData.tShirtSize) {
    //   errors.tShirtSize = "Please select a T-shirt size";
    // }
    
    if (!formData.travelMode) {
      errors.travelMode = "Please select your travel mode";
    }
    
    if (!formData.arrivalDateTime) {
      errors.arrivalDateTime = "Please select your expected arrival date and time";
    }
    
    if (!formData.agreeToTerms) {
      errors.agreeToTerms = "You must agree to the terms and conditions";
    }
    
    // If there are errors, show them and stop submission
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    // Submit form - API call
    setLoading(true);
    setApiError('');
    
    try {
      // Prepare data for API
      const travelRegistrationData = {
        mobileNumber: formData.mobileNumber,
        travelerName: formData.travelerName,
        alternateMobile: formData.alternateMobile,
        // tShirtSize: formData.tShirtSize,
        travelMode: formData.travelMode,
        expectedArrivalDateTime: formData.arrivalDateTime,
        agreedToTerms: formData.agreeToTerms
      };
      
      // Submit travel registration data to API
      const response = await axios.post(
        // `${API_BASE_URL}/travel-registrations`, 
        `${TRAVEL_REGISTRATION}`, 
        travelRegistrationData
      );
      
      if (response.data.status === "201") {
        // Set registration number from API response
        const registrationNumber = response.data.data.registrationNumber;
        setFormData(prev => ({...prev, registrationNumber}));
        setFormSubmitted(true);
      } else {
        setApiError(response.data.message || "Registration failed, please try again.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      
      if (error.response) {
        // The request was made and the server responded with an error
        setApiError(`Error: ${error.response.data.message || 'Registration failed'}`);
      } else if (error.request) {
        // The request was made but no response was received
        setApiError("Network error. Please check your connection and try again.");
      } else {
        // Something else happened
        setApiError(`Error: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setFormData({
      mobileNumber: "",
      farmerName: "",
      farmCode: "",
      location: "",
      travelerName: "",
      alternateMobile: "",
      tShirtSize: "",
      travelMode: "",
      arrivalDateTime: "",
      agreeToTerms: false
    });
    setRegisteredUser(false);
    setNumberChecked(false);
    setFormSubmitted(false);
    setValidationErrors({});
    setApiError('');
  };

  // Success message component
  if (formSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-green-600 p-6">
            <div className="flex justify-center">
              <div className="rounded-full bg-white p-2">
                <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
            </div>
            <h2 className="mt-4 text-center text-white text-2xl font-bold">Registration Successful!</h2>
          </div>
          
          <div className="p-6">
            <div className="mb-6 text-center">
              <p className="text-gray-700">Thank you for registering for the event. We've sent the details to your mobile number.</p>
              
              {/* Registration Number */}
              <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Your Registration Number</p>
                <div className="flex items-center justify-center space-x-1">
                  <p className="text-2xl font-bold text-gray-900 tracking-wider">{formData.registrationNumber}</p>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(formData.registrationNumber);
                      alert("Registration number copied to clipboard!");
                    }}
                    className="text-blue-600 hover:text-blue-800"
                    title="Copy to clipboard"
                  >
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                      <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                    </svg>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Please save this number for future reference</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="border-t border-b border-gray-200 py-3">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-500">Name:</span>
                  <span className="text-gray-900">{formData.farmerName}</span>
                </div>
              </div>
              
              <div className="border-b border-gray-200 pb-3">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-500">Mobile:</span>
                  <span className="text-gray-900">{formData.mobileNumber}</span>
                </div>
              </div>
              
              <div className="border-b border-gray-200 pb-3">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-500">Traveler:</span>
                  <span className="text-gray-900">{formData.travelerName}</span>
                </div>
              </div>
              
              <div className="border-b border-gray-200 pb-3">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-500">Travel Mode:</span>
                  <span className="text-gray-900">{formData.travelMode}</span>
                </div>
              </div>
              
              <div className="border-b border-gray-200 pb-3">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-500">Arrival:</span>
                  <span className="text-gray-900">{new Date(formData.arrivalDateTime).toLocaleString()}</span>
                </div>
              </div>
            </div>
            
            <button onClick={handleReset} className="mt-6 w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-4 focus:ring-green-300 transition duration-200">
              Register Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-green-500 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {/* <div className="h-12 w-12 bg-white rounded-full flex items-center justify-center"> */}
                  {/* <svg className="h-8 w-8 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg> */}
                  <img
                                src={companySymbol}
                                alt="Company Symbol"
                                className="h-14 w-14 p-1 bg-white rounded-full shadow-lg"
                              />
                {/* </div> */}
                <h1 className="text-2xl font-bold text-white">Poultry Conclave Registration Form</h1>
              </div>
              {/* <div className="hidden sm:flex items-center space-x-2">
                <div className="bg-white/20 rounded-lg px-3 py-1">
                  <span className="text-white text-sm font-medium">Event Registration</span>
                </div>
                <div className="bg-white/20 rounded-lg px-3 py-1 flex items-center">
                  <svg className="h-3 w-3 text-white mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  <span className="text-white text-sm font-medium">123456789</span>
                </div>
              </div> */}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6">
            <div className="mb-8">
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-800">
                      Please enter your registered mobile number to continue.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:space-x-4">
                <div className="flex-1 mb-4 sm:mb-0">
                  <label htmlFor="mobileNumber" className="block text-sm font-medium text-gray-700 mb-1">
                    Registered Mobile Number*
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      id="mobileNumber"
                      name="mobileNumber"
                      value={formData.mobileNumber}
                      onChange={handleChange}
                      placeholder="Enter 10 digit number"
                      className={`pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm ${
                        validationErrors.mobileNumber ? 'border-red-300' : 'border-gray-300'
                      }`}
                      disabled={loading || registeredUser}
                      inputMode="numeric"
                    />
                  </div>
                  {validationErrors.mobileNumber && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.mobileNumber}</p>
                  )}
                </div>
                <div className="sm:self-end mb-4 sm:mb-0">
                  <button
                    type="button"
                    onClick={checkMobileNumber}
                    disabled={loading || registeredUser || !formData.mobileNumber}
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Verifying...
                      </>
                    ) : registeredUser ? (
                      <>
                        <svg className="h-4 w-4 mr-1.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        Verified
                      </>
                    ) : (
                      "Verify Number"
                    )}
                  </button>
                </div>
              </div>
            </div>

            {numberChecked && !registeredUser && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">
                      {apiError || "This mobile number is not registered. Please enter a registered mobile number or contact support for assistance."}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {registeredUser && (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <h3 className="text-lg font-medium text-green-800 mb-2">Partner Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Name</p>
                      <p className="font-medium">{formData.farmerName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Registered Partner Code</p>
                      <p className="font-medium">{formData.farmCode}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Location</p>
                      <p className="font-medium">{formData.location}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <h3 className="text-lg font-medium leading-6 text-gray-900 border-b pb-2">Travel Information</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="travelerName" className="block text-sm font-medium text-gray-700">
                        Name of Traveler*
                      </label>
                      <input
                        type="text"
                        id="travelerName"
                        name="travelerName"
                        value={formData.travelerName}
                        onChange={handleChange}
                        className={`mt-1 block w-full rounded-md shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm ${
                          validationErrors.travelerName ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {validationErrors.travelerName && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.travelerName}</p>
                      )}
                    </div>
                    
                    <div>
                      <label htmlFor="alternateMobile" className="block text-sm font-medium text-gray-700">
                        Alternate Mobile Number*
                      </label>
                      <input
                        type="text"
                        id="alternateMobile"
                        name="alternateMobile"
                        value={formData.alternateMobile}
                        onChange={handleChange}
                        placeholder="Enter 10 digit number"
                        className={`mt-1 block w-full rounded-md shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm ${
                          validationErrors.alternateMobile ? 'border-red-300' : 'border-gray-300'
                        }`}
                        inputMode="numeric"
                      />
                      {validationErrors.alternateMobile && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.alternateMobile}</p>
                      )}
                    </div>
                    
                    {/* <div>
                      <label htmlFor="tShirtSize" className="block text-sm font-medium text-gray-700">
                        T-Shirt Size*
                      </label>
                      <select
                        id="tShirtSize"
                        name="tShirtSize"
                        value={formData.tShirtSize}
                        onChange={handleChange}
                        className={`mt-1 block w-full rounded-md shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm ${
                          validationErrors.tShirtSize ? 'border-red-300' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select size</option>
                        <option value="S">S - Small</option>
                        <option value="M">M - Medium</option>
                        <option value="L">L - Large</option>
                        <option value="XL">XL - Extra Large</option>
                        <option value="XXL">XXL - Double Extra Large</option>
                      </select>
                      {validationErrors.tShirtSize && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.tShirtSize}</p>
                      )}
                    </div> */}
                    
                    <div>
                      <label htmlFor="travelMode" className="block text-sm font-medium text-gray-700">
                        Travel Mode*
                      </label>
                      <select
                        id="travelMode"
                        name="travelMode"
                        value={formData.travelMode}
                        onChange={handleChange}
                        className={`mt-1 block w-full rounded-md shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm ${
                          validationErrors.travelMode ? 'border-red-300' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select mode</option>
                        <option value="Air">Air</option>
                        <option value="Train">Train</option>
                        <option value="Road">Road</option>
                      </select>
                      {validationErrors.travelMode && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.travelMode}</p>
                      )}
                    </div>
                    
                    <div className="md:col-span-2">
                      <label htmlFor="arrivalDateTime" className="block text-sm font-medium text-gray-700">
                        Expected Arrival Date and Time*
                      </label>
                      <input
                        type="datetime-local"
                        id="arrivalDateTime"
                        name="arrivalDateTime"
                        value={formData.arrivalDateTime}
                        onChange={handleChange}
                        className={`mt-1 block w-full rounded-md shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm ${
                          validationErrors.arrivalDateTime ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {validationErrors.arrivalDateTime && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.arrivalDateTime}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="pt-4">
                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="agreeToTerms"
                          name="agreeToTerms"
                          type="checkbox"
                          checked={formData.agreeToTerms}
                          onChange={handleChange}
                          className={`h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded ${
                            validationErrors.agreeToTerms ? 'border-red-300' : ''
                          }`}
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="agreeToTerms" className="font-medium text-gray-700">
                          I agree to the terms and conditions*
                        </label>
                        {validationErrors.agreeToTerms && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.agreeToTerms}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Display API-specific errors */}
                  {apiError && (
                    <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-red-800">{apiError}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="pt-5">
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handleReset}
                        className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        Reset
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-green-300"
                      >
                        {loading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Submitting...
                          </>
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Terms and Conditions */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              {/* Support Contact Information */}
              <div className="mb-4 bg-blue-50 border border-blue-200 rounded-md p-4 flex items-center">
                <svg className="h-5 w-5 text-blue-500 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                <p className="text-blue-700 font-medium">For any queries please call - 7610255555</p>
              </div>
              
              <h4 className="text-sm font-medium text-gray-900">Please Note:</h4>
              <ul className="mt-2 text-xs text-gray-500 list-disc pl-5 space-y-1">
                <li>Admits 1 person, per partner code.</li>
                <li>Partner to arrange "To & Fro" travel for self.</li>
                <li>Accommodation & Food, arranged by the company.</li>
                <li>Kindly verify your details before submitting the registration form.</li>
                <li>Details provided in this form will be used to confirm your stay at the respective hotels.</li>
                <li> Please download the event schedule -  

                 
                          <a 
                          
                          href={PoultryConclave}
                          download="PoultryConclave.jpg"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 text-sm  hover:text-green-600 whitespace-nowrap"
                        >
                        Poultry Conclave
                          </a>
                         
                </li>
              </ul>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FarmerTravelForm;